import { LitElement, html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-switch.styles';
import { SDS_SIZES } from '../types';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';

import '../sds-icon/sds-icon';

type v2SwitchSizes = Extract<SDS_SIZES, 'sm' | 'md' | 'lg'>;
type SwitchSize = v2SwitchSizes | 's' | 'l';

export interface SDSSwitchProps {
	disabled?: boolean;
	label?: string;
	'hide-label'?: boolean;
	name?: string;
	readonly?: boolean;
	reverse?: boolean;
	checked?: boolean;
	size?: SwitchSize;
}

@guardedCustomElement('sds-switch')
export class SDSSwitch extends SDSEventDispatcherMixin(LitElement) {
	static override styles = [styles];

	@property({ type: Boolean, reflect: true }) disabled: boolean = false;
	@property({ type: String, reflect: true }) label: string = '';
	@property({ type: Boolean, reflect: true, attribute: 'hide-label' }) hideLabel: boolean = false;
	@property({ type: String, reflect: true }) name?: string;
	@property({ type: Boolean, reflect: true }) reverse: boolean = false;
	@property({ type: Boolean, reflect: true }) checked: boolean = false;
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: boolean = false;
	@property({ type: String, reflect: true }) size: SwitchSize = 'md';

	@query('#control') control!: HTMLInputElement;

	static formAssociated = true;
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};
	private _initialChecked: boolean = false;

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override connectedCallback(): void {
		super.connectedCallback();
		this._initialChecked = this.checked;
		this.internals.setFormValue(this.checked ? 'on' : 'off');
	}

	override updated(changedProperties: Map<string | number | symbol, unknown>) {
		if (changedProperties.has('checked')) {
			this.control.checked = this.checked;
			this.internals.setFormValue(this.checked ? 'on' : 'off');
		}
	}

	formResetCallback() {
		this.checked = this._initialChecked;
		this.control.checked = this._initialChecked;
		this.internals.setFormValue(this._initialChecked ? 'on' : 'off');
	}

	formStateRestoreCallback(state: unknown) {
		this.checked = state as boolean;
	}

	private readonly _handleChange = (event: Event) => {
		event.stopImmediatePropagation();
		if (!this.disabled) {
			const elem = event.target as HTMLInputElement;
			this.checked = elem.checked;
			this.internals.setFormValue(this.checked ? 'on' : 'off');
			this.dispatchSheriffEvent('change');
		}
	};

	private readonly _handleFocus = (event: Event) => {
		event.stopImmediatePropagation();
		if (!this.disabled) {
			this.dispatchSheriffEvent('focus');
		}
	};

	private readonly _handleBlur = (event: Event) => {
		event.stopImmediatePropagation();
		if (!this.disabled && !this.disabled) {
			this.dispatchSheriffEvent('blur');
		}
	};

	private readonly getIconSize = (): v2SwitchSizes => {
		switch (this.size) {
			case 'sm':
			case 'md':
			case 'lg':
				return this.size;
			case 'l':
				return 'lg';
			case 's':
				return 'sm';
		}
	};

	get value(): string {
		return this.checked ? 'on' : 'off';
	}

	override render() {
		const switchClasses = {
			'sds-switch': true,
		};

		const labelClasses = {
			'sds-switch__label': true,
			'sds-switch__label--hidden': this.hideLabel,
		};

		const inputTemplate = html`<input
			class="sds-switch__input"
			id="control"
			type="checkbox"
			role="switch"
			?readonly="${this.readOnly}"
			?disabled="${this.disabled}"
			.checked="${this.checked}"
			?checked="${this.checked}"
			name="${ifDefined(this.name)}"
			@change="${this._handleChange}"
			@focus="${this._handleFocus}"
			@blur="${this._handleBlur}"
		/>`;
		const iconTemplate = this.readOnly ? html`<sds-icon name="lock-material-filled" size="${this.getIconSize()}"></sds-icon>` : nothing;
		const labelTemplate = html`<label class="${classMap(labelClasses)}" for="control">${iconTemplate}${this.label}</label>`;
		return html`<div class="${classMap(switchClasses)}">${this.reverse ? [labelTemplate, inputTemplate] : [inputTemplate, labelTemplate]}</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-switch': SDSSwitch;
	}
}
